/**
 * WebMCP (navigator.modelContext) — expose read-only Roamzy tools to in-browser
 * AI agents (e.g. Claude in Chrome) when they're on roamzy.io.
 *
 * Feature-detected: a complete NO-OP in browsers without the experimental
 * `navigator.modelContext` API (≈ all stable browsers today). Read-only
 * discovery tools ONLY — no purchase/mutation from the page surface; that path
 * stays behind the authenticated REST API + the remote MCP endpoint. Mirrors a
 * subset of the canonical MCP tools (single source of truth = shared/mcp-core).
 */
const API = '/api/v1';

interface ToolResult { content: Array<{ type: 'text'; text: string }>; }
interface WebMcpTool {
  name: string;
  description: string;
  inputSchema: { type: 'object'; properties: Record<string, unknown>; required?: string[] };
  execute: (args: unknown) => Promise<ToolResult>;
}

async function getJson(path: string): Promise<unknown> {
  const res = await fetch(path, { headers: { accept: 'application/json' }, credentials: 'include' });
  return res.json();
}
function asText(data: unknown): ToolResult {
  return { content: [{ type: 'text', text: JSON.stringify(data) }] };
}

const TOOLS: WebMcpTool[] = [
  {
    name: 'roamzy_status',
    description: 'Roamzy API status, including whether purchases are paused. Call before any purchase.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => asText(await getJson(`${API}/status`)),
  },
  {
    name: 'roamzy_list_countries',
    description: 'List supported countries / coverage and per-megabyte pricing for the Roamzy global eSIM.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => asText(await getJson(`${API}/catalog`)),
  },
  {
    name: 'roamzy_payment_options',
    description: 'Available payment options (USDT networks) for Roamzy top-ups.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => asText(await getJson(`${API}/payment-options`)),
  },
];

type ModelContext = {
  provideContext?: (ctx: { tools: WebMcpTool[] }) => void;
  registerTool?: (tool: WebMcpTool) => void;
};

/** Register Roamzy's read-only tools with the browser agent runtime. No-op if
 *  the experimental API is absent. Safe to call on every page load. */
export function initWebMcp(): void {
  if (typeof navigator === 'undefined') return;
  const mc = (navigator as unknown as { modelContext?: ModelContext }).modelContext;
  if (!mc) return; // not supported → no-op
  try {
    if (typeof mc.provideContext === 'function') {
      mc.provideContext({ tools: TOOLS });
    } else if (typeof mc.registerTool === 'function') {
      for (const t of TOOLS) mc.registerTool(t);
    }
  } catch {
    /* experimental API — never break the page */
  }
}
